import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import api from '../utils/api';

/**
 * Single source of truth for the degree / specialisation catalog.
 *
 * The catalog is reference data that changes a few times a year, so it is
 * fetched once per session and reused everywhere instead of every dropdown
 * pulling the full collection on mount. Results are mirrored into
 * sessionStorage so route changes and refreshes are instant.
 */

const DegreeContext = createContext(null);

// Bump when the shape the app depends on changes, so old tabs don't serve a
// payload that is missing fields the selectors now read.
const CACHE_KEY = 'smaart.degreeCatalog.v2';

const readCache = () => {
  try {
    const raw = sessionStorage.getItem(CACHE_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    if (!Array.isArray(parsed) || parsed.length === 0) return null;
    // Self-heal: a cache written before the catalog seed has no courseId, which
    // would leave every eligibility dropdown empty. Refetch instead.
    if (!parsed.some((d) => d && d.courseId)) return null;
    return parsed;
  } catch {
    return null;
  }
};

const writeCache = (degrees) => {
  try {
    sessionStorage.setItem(CACHE_KEY, JSON.stringify(degrees));
  } catch {
    // Quota or private mode — the in-memory copy still serves this session.
  }
};

export const DegreeProvider = ({ children }) => {
  const [degrees, setDegrees] = useState(() => readCache() || []);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const inFlight = useRef(null);

  const loadDegrees = useCallback(async ({ force = false } = {}) => {
    if (!force && degrees.length > 0) return degrees;
    // Collapse concurrent mounts into a single network call.
    if (inFlight.current) return inFlight.current;

    setLoading(true);
    setError(null);

    inFlight.current = api
      .get('/degrees')
      .then(({ data }) => {
        const list = data?.data || [];
        setDegrees(list);
        writeCache(list);
        return list;
      })
      .catch((err) => {
        setError(err);
        return [];
      })
      .finally(() => {
        setLoading(false);
        inFlight.current = null;
      });

    return inFlight.current;
  }, [degrees]);

  useEffect(() => {
    if (degrees.length === 0) loadDegrees();
    // Intentionally runs once — loadDegrees short-circuits when already loaded.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const indexes = useMemo(() => {
    const levels = [];
    const byLevel = new Map();          // level -> Set(domain)
    const byDomain = new Map();         // `${level}|${domain}` -> Map(abbreviation -> fullName)
    const byDegree = new Map();         // `${level}|${domain}|${abbr}` -> [{ courseId, specialization }]
    const byCourseId = new Map();

    for (const d of degrees) {
      const level = d.level || 'Other';
      const domain = d.domain || 'Other';
      const abbr = d.abbreviation || d.fullName || 'Other';

      if (!byLevel.has(level)) {
        byLevel.set(level, new Set());
        levels.push(level);
      }
      byLevel.get(level).add(domain);

      const domainKey = `${level}|${domain}`;
      if (!byDomain.has(domainKey)) byDomain.set(domainKey, new Map());
      byDomain.get(domainKey).set(abbr, d.fullName || abbr);

      const degreeKey = `${level}|${domain}|${abbr}`;
      if (!byDegree.has(degreeKey)) byDegree.set(degreeKey, []);
      byDegree.get(degreeKey).push({
        courseId: d.courseId || d.uniqueId,
        specialization: d.specialization || 'General',
        duration: d.duration
      });

      if (d.courseId) byCourseId.set(d.courseId, d);
    }

    return { levels, byLevel, byDomain, byDegree, byCourseId };
  }, [degrees]);

  const getLevels = useCallback(() => indexes.levels, [indexes]);

  const getDomains = useCallback(
    (level) => Array.from(indexes.byLevel.get(level) || []).sort(),
    [indexes]
  );

  const getDegreeNames = useCallback(
    (level, domain) =>
      Array.from((indexes.byDomain.get(`${level}|${domain}`) || new Map()).entries())
        .map(([abbreviation, fullName]) => ({ abbreviation, fullName }))
        .sort((a, b) => a.abbreviation.localeCompare(b.abbreviation)),
    [indexes]
  );

  const getSpecializations = useCallback(
    (level, domain, abbreviation) =>
      (indexes.byDegree.get(`${level}|${domain}|${abbreviation}`) || [])
        .slice()
        .sort((a, b) => a.specialization.localeCompare(b.specialization)),
    [indexes]
  );

  /** Every specialisation for a degree, across domains — for flat degree pickers. */
  const getSpecializationsByAbbreviation = useCallback(
    (abbreviation) => {
      const seen = new Set();
      const out = [];
      for (const d of degrees) {
        if (d.abbreviation !== abbreviation) continue;
        const spec = d.specialization || 'General';
        if (seen.has(spec)) continue;
        seen.add(spec);
        out.push({ courseId: d.courseId || d.uniqueId, specialization: spec });
      }
      return out.sort((a, b) => a.specialization.localeCompare(b.specialization));
    },
    [degrees]
  );

  /**
   * The curated UG catalog only — the 274 records seeded from
   * Backend/data/ugDegreeCatalog.js, identified by carrying a `courseId`.
   *
   * The full `degrees` list also holds PG, Diploma, Doctoral and legacy
   * imported rows. Those are right for college degree registration but wrong
   * for job eligibility pickers, which is why the helpers below read from this
   * subset instead. `sourceDegree` is the degree label as written in the source
   * catalog (BBA, MBBS, B.Arch), so the dropdowns show exactly those names.
   */
  const catalogDegrees = useMemo(
    () => degrees.filter((d) => d.courseId && d.sourceDegree),
    [degrees]
  );

  /**
   * The 31 catalog degrees as { value, label } for eligibility pickers.
   *
   * The label shows both forms — "B.E. (Bachelor of Engineering)" — but the
   * VALUE that gets saved is the full name alone, because that is exactly what
   * a student record stores (Student.academic.degreeGroup = CollegeDegree
   * .fullName). Storing the label instead would force substring matching, and
   * "Bachelor of Science" sits inside "Bachelor of Science in Agriculture",
   * so a plain B.Sc. student would wrongly match an Agriculture Honours job.
   * Identical stored values on both sides means matching is a plain equality
   * check with no false positives.
   */
  const getDegreeOptions = useCallback(() => {
    const byFullName = new Map();

    for (const d of catalogDegrees) {
      const value = d.fullName?.trim();
      const short = d.sourceDegree?.trim();
      if (!value || byFullName.has(value)) continue;
      byFullName.set(value, {
        value,
        short: short || value,
        // "B.Sc. Nursing" is its own full name — don't render it twice.
        label: short && short !== value ? `${short} (${value})` : value
      });
    }

    return Array.from(byFullName.values()).sort((a, b) => a.short.localeCompare(b.short));
  }, [catalogDegrees]);

  /** Human label for a value already saved on a job, falling back to itself. */
  const getDegreeLabel = useCallback(
    (value) => {
      const key = String(value || '').trim();
      if (!key) return '';
      const hit = getDegreeOptions().find((o) => o.value === key);
      return hit ? hit.label : key;
    },
    [getDegreeOptions]
  );

  /**
   * Specialisations valid for the given degrees, keyed by the same full-name
   * value the pickers save.
   *
   * Passing an empty list returns every specialisation in the catalog — that is
   * the "no degree chosen yet, show everything" case. Eligibility pickers use
   * this so a job restricted to B.E. can't be tagged with a B.Com. branch.
   */
  const getSpecializationsForDegrees = useCallback(
    (degreeValues = []) => {
      const wanted = (Array.isArray(degreeValues) ? degreeValues : [degreeValues])
        .map((a) => String(a || '').trim())
        .filter(Boolean);

      const source = wanted.length
        ? catalogDegrees.filter((d) => {
            const full = d.fullName?.trim();
            const short = d.sourceDegree?.trim();
            // Accept the short form too, so job rows saved before this change
            // still resolve to their branches while being edited.
            return (full && wanted.includes(full)) || (short && wanted.includes(short));
          })
        : catalogDegrees;

      return Array.from(
        new Set(source.map((d) => d.specialization?.trim()).filter(Boolean))
      ).sort();
    },
    [catalogDegrees]
  );

  const getByCourseId = useCallback((courseId) => indexes.byCourseId.get(courseId) || null, [indexes]);

  const value = useMemo(
    () => ({
      degrees,
      catalogDegrees,
      loading,
      error,
      reload: () => loadDegrees({ force: true }),
      getLevels,
      getDomains,
      getDegreeNames,
      getSpecializations,
      getSpecializationsByAbbreviation,
      getDegreeOptions,
      getDegreeLabel,
      getSpecializationsForDegrees,
      getByCourseId
    }),
    [
      degrees,
      catalogDegrees,
      loading,
      error,
      loadDegrees,
      getLevels,
      getDomains,
      getDegreeNames,
      getSpecializations,
      getSpecializationsByAbbreviation,
      getDegreeOptions,
      getDegreeLabel,
      getSpecializationsForDegrees,
      getByCourseId
    ]
  );

  return <DegreeContext.Provider value={value}>{children}</DegreeContext.Provider>;
};

export const useDegrees = () => {
  const ctx = useContext(DegreeContext);
  if (!ctx) throw new Error('useDegrees must be used inside a <DegreeProvider>');
  return ctx;
};

export default DegreeContext;
